/* eslint-disable no-undef */
import React, { useState, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  CircularProgress,
  Alert,
  Fade
} from '@mui/material';
import {
  CloudUpload,
  InsertDriveFile,
  Close,
  CheckCircle
} from '@mui/icons-material';
import { useDropzone } from 'react-dropzone';
import { useMutation } from '@tanstack/react-query';
import { useAuthenticatedFetch } from '../../hooks/useAuthenticatedFetch';
import { createApiClient } from '../../utils/api';
import { useSnackbar } from '../../contexts/NotificationContext';
import { ProcessingStatusDialog } from './ProcessingStatusDialog';

interface MarketReportDropzoneProps {
  marketId: number;
  marketName: string;
  onProcessingComplete: () => void;
  compact?: boolean;
  goldBranding?: boolean;
}

export const MarketReportDropzone: React.FC<MarketReportDropzoneProps> = ({
  marketId,
  marketName,
  onProcessingComplete,
  compact = false,
  goldBranding = false
}) => {
  const { showSnackbar } = useSnackbar();
  const { authenticatedFetch } = useAuthenticatedFetch();
  const api = createApiClient(authenticatedFetch);
  
  const [file, setFile] = useState<File | null>(null);
  const [operationId, setOperationId] = useState<string | null>(null);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);

  const uploadMutation = useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post(
        `/api/marketcompass/admin/markets/${marketId}/process-report`,
        formData
      );
      
      return response.data;
    },
    onSuccess: (data) => {
      setOperationId(data.operationId);
      setStatusDialogOpen(true);
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.error || 'Failed to process report';
      showSnackbar(errorMessage, 'error');
      setFile(null);
    }
  });

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const droppedFile = acceptedFiles[0];
    if (!droppedFile) return;

    // Validate file type
    if (!droppedFile.name.toLowerCase().endsWith('.pdf')) {
      showSnackbar('Only PDF files are allowed', 'error');
      return;
    }

    // Validate file size (50MB)
    const maxSize = 50 * 1024 * 1024;
    if (droppedFile.size > maxSize) {
      showSnackbar('File size must be less than 50MB', 'error');
      return;
    }

    // Check if filename contains month/year pattern
    const hasMonthPattern = /\d{6}/.test(droppedFile.name) ||
                           /\d{4}[-_]\d{2}/.test(droppedFile.name) ||
                           /(january|february|march|april|may|june|july|august|september|october|november|december)[-_]?\d{4}/i.test(droppedFile.name) ||
                           /(jan|feb|mar|apr|may|jun|jul|aug|sep|sept|oct|nov|dec)[-_\s]\d{4}/i.test(droppedFile.name);

    if (!hasMonthPattern) {
      showSnackbar(
        'Filename must contain a date (e.g., 202508, 2025-08, August 2025, or Aug 2025)',
        'warning'
      );
    }

    setFile(droppedFile);
    uploadMutation.mutate(droppedFile);
  }, [marketId, showSnackbar, uploadMutation]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf']
    },
    maxFiles: 1,
    disabled: uploadMutation.isPending
  });

  const handleRemoveFile = () => {
    setFile(null);
    uploadMutation.reset();
  };

  const handleProcessingComplete = () => {
    // Just notify parent that processing is complete
    // Don't close the dialog - let the user do that
    onProcessingComplete();
  };
  
  const handleDialogClose = () => {
    setStatusDialogOpen(false);
    setFile(null);
    setOperationId(null);
    uploadMutation.reset();
  };

  // Render compact version
  if (compact) {
    const goldColor = '#F5A800';
    const baseStyles = goldBranding ? {
      display: 'inline-flex',
      alignItems: 'center',
      gap: 1,
      px: 2,
      py: 1,
      border: '2px solid',
      borderColor: isDragActive ? '#D89600' : goldColor,
      borderRadius: '3px',
      backgroundColor: isDragActive ? '#D89600' : goldColor,
      color: 'white',
      cursor: uploadMutation.isPending ? 'not-allowed' : 'pointer',
      transition: 'all 0.3s ease',
      '&:hover': {
        borderColor: '#D89600',
        backgroundColor: '#D89600'
      }
    } : {
      display: 'inline-flex',
      alignItems: 'center',
      gap: 1,
      px: 2,
      py: 1,
      border: '1px solid',
      borderColor: isDragActive ? 'primary.main' : 'divider',
      borderRadius: 1,
      backgroundColor: isDragActive ? 'action.hover' : 'background.paper',
      cursor: uploadMutation.isPending ? 'not-allowed' : 'pointer',
      transition: 'all 0.3s ease',
      '&:hover': {
        borderColor: 'primary.main',
        backgroundColor: 'action.hover'
      }
    };

    return (
      <>
        <Box
          {...getRootProps()}
          sx={baseStyles}
        >
          <input {...getInputProps()} />
          {uploadMutation.isPending ? (
            <CircularProgress size={20} sx={goldBranding ? { color: 'white' } : {}} />
          ) : (
            <CloudUpload fontSize="small" sx={goldBranding ? { color: 'white' } : {}} />
          )}
          <Typography variant="body2" sx={goldBranding ? { color: 'white', fontWeight: 500 } : {}}>
            {uploadMutation.isPending ? 'Uploading...' : 'Upload Report'}
          </Typography>
        </Box>

        {operationId && (
          <ProcessingStatusDialog
            open={statusDialogOpen}
            operationId={operationId}
            onClose={handleDialogClose}
            onComplete={handleProcessingComplete}
          />
        )}
      </>
    );
  }

  // Render full version
  return (
    <>
      <Paper
        {...getRootProps()}
        sx={{
          p: 3,
          mb: 3,
          border: '2px dashed',
          borderColor: isDragActive ? 'primary.main' : 'divider',
          backgroundColor: isDragActive ? 'action.hover' : 'background.paper',
          cursor: uploadMutation.isPending ? 'not-allowed' : 'pointer',
          transition: 'all 0.3s ease',
          '&:hover': {
            borderColor: 'primary.main',
            backgroundColor: 'action.hover'
          }
        }}
      >
        <input {...getInputProps()} />
        
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center'
          }}
        >
          {uploadMutation.isPending ? (
            <CircularProgress size={48} sx={{ mb: 2 }} />
          ) : file ? (
            <CheckCircle color="success" sx={{ fontSize: 48, mb: 2 }} />
          ) : (
            <CloudUpload sx={{ fontSize: 48, mb: 2, color: 'text.secondary' }} />
          )}

          <Typography variant="h6" gutterBottom>
            {uploadMutation.isPending
              ? 'Uploading...'
              : file
              ? 'File uploaded successfully'
              : 'Drop New MarketCompass Report'}
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {uploadMutation.isPending
              ? 'Please wait while we process your report'
              : file
              ? `Processing: ${file.name}`
              : 'Drag and drop a PDF file here, or click to select'}
          </Typography>

          {file && !uploadMutation.isPending && (
            <Fade in>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <InsertDriveFile color="action" />
                <Typography variant="body2" sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {file.name}
                </Typography>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveFile();
                  }}
                >
                  <Close fontSize="small" />
                </IconButton>
              </Box>
            </Fade>
          )}

          {!uploadMutation.isPending && (
            <Alert severity="info" sx={{ mt: 2, maxWidth: 500 }}>
              <Typography variant="body2">
                The report will be automatically processed to generate descriptions, key findings, and a podcast for {marketName}.
                Filename should contain the report month (e.g., 202508, 2025-08, August 2025, or Aug 2025).
              </Typography>
            </Alert>
          )}
        </Box>
      </Paper>

      {operationId && (
        <ProcessingStatusDialog
          open={statusDialogOpen}
          operationId={operationId}
          onClose={handleDialogClose}
          onComplete={handleProcessingComplete}
        />
      )}
    </>
  );
};